import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { SourcePlatform } from 'graphql/generated';
import { ForgotPasswordForm } from '@core/types/authentication-form.type';
import { passwordMatchingValidator } from '@app/core/validators/password-matching.validator';
import { AuthService } from '@app/core/services/auth.service';
import { FormPinComponent } from '@app/core/components/forms/pin/form-pin.component';
import { FormPasswordComponent } from '@app/core/components/forms/password/form-password.component';

@Component({
  standalone: true,
  selector: 'ups-forgot-password-modal',
  templateUrl: './auth-forgot-password-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonicModule,
    TranslocoModule,
    FormPinComponent,
    FormPasswordComponent, 
  ]
})
export class ForgotPasswordModalComponent implements OnInit, OnDestroy {
  
  @Input() public creds!: string;
  @Input() public strategy!: 'email' | 'mobileNumber';
  @ViewChild('btn') protected btn!: ElementRef;
  public readonly form: FormGroup<ForgotPasswordForm> = new FormGroup({
    otp: new FormControl('', { nonNullable: true, validators: [ Validators.required, Validators.minLength(6) ]}),
    confirm: new FormControl('', { nonNullable: true, validators: [ Validators.required, Validators.minLength(8) ]}),
    password: new FormControl('', { nonNullable: true, validators: [ Validators.required, Validators.minLength(8) ]})
  }, { validators: passwordMatchingValidator });
  public second: number = 0;
  protected interval: any;
  protected sub?: Subscription;

  constructor(
    protected readonly ch: ChangeDetectorRef,
    protected readonly authService: AuthService,
    protected readonly modalCtrl: ModalController,
    protected readonly alertCtrl: AlertController,
  ) { }

  public ngOnInit(): void{
    history.pushState({ forgot: true }, '');
    this.sendOtp();
  }

  public ngOnDestroy(): void{ 
    this.sub?.unsubscribe();
    if (history.state.forgot) history.back();
  }

  @HostListener('window:popstate')
  public back(): void{ this.modalCtrl.dismiss(); }

  public resend(): void{
    if (this.second === 0) {
      this.second = 60;

      this.interval = setInterval(() => {
        this.second--;
        if (this.second === 0) clearInterval(this.interval);
        this.ch.detectChanges();
      }, 1000);
      
      this.sendOtp();
    }
  }

  public confirm(): void{
    if (!this.form.valid) return;

    this.toggleBtn();
    this.sub?.unsubscribe();

    this.sub = this.authService.confirmForgotPass({ verifCode: this.form.value.otp!, principalId: this.creds, newPassword: this.form.value.password!, sourcePlatform: SourcePlatform.CpCommerce }).subscribe({ 
      next:() => {
        this.showAlert(translate('message.update_success', { field: translate('form.password') }), translate('message.new_password_login'));
        this.back();
      },
      error: ({ message }) => {
        this.toggleBtn();
        this.showAlert(translate('title.error'), message);
      }
    });
  }

  protected toggleBtn(): void{
    this.btn?.nativeElement.toggleAttribute('disabled');
    this.btn?.nativeElement.classList.toggle('btn-loading');
  }

  protected sendOtp(): void{
    this.authService.reqForgotPass({ [this.strategy]: this.creds, sourcePlatform: SourcePlatform.CpCommerce }).subscribe({ 
      next:() => this.showAlert(translate('title.success'), translate('message.verif_code_sent')),
      error: ({ message }) => {
        this.showAlert(translate('title.error'), message);
        this.back();
      }
    });
  }

  protected async showAlert(header: string, message: string): Promise<void>{ (await this.alertCtrl.create({ header, message, buttons: [{ text:'OK' }] })).present(); }
}
